import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Appointment } from '@/context/types/PetTypes';
import { addHours, addDays, parseISO } from 'date-fns';
import { logger } from '@/utils/logger';

/**
 * Hook pour synchroniser les rendez-vous avec la table user_reminders.
 * Crée automatiquement 2 rappels par RDV :
 *   index 0 → J-1 (24h avant)
 *   index 1 → H-1 (1h avant)
 */
export function useReminderSync() {
  const { user } = useAuth();

  const getAppointmentDateTime = (date: string, time?: string): Date => {
    const timeStr = time || '09:00';
    return parseISO(`${date}T${timeStr}:00`);
  };

  const deleteReminder = useCallback(async (appointmentId: string) => {
    if (!user) return;
    const { error } = await supabase
      .from('user_reminders')
      .delete()
      .eq('user_id', user.id)
      .eq('related_event_id', appointmentId);

    if (error) {
      logger.error('useReminderSync: Erreur suppression rappel:', error);
    }
  }, [user]);

  const syncReminder = useCallback(async (appointment: Appointment) => {
    if (!user) return;

    if (!appointment.reminderEnabled) {
      await deleteReminder(appointment.id);
      return;
    }

    const apptDt = getAppointmentDateTime(appointment.date, appointment.time);
    const dayBefore = addDays(apptDt, -1);
    const hourBefore = addHours(apptDt, -1);

    const reminders = [
      {
        reminder_index: 0,
        due_at: dayBefore.toISOString(),
        title: `Rappel demain : ${appointment.title}`,
        message: appointment.location
          ? `${appointment.title} — ${appointment.location}`
          : appointment.title,
      },
      {
        reminder_index: 1,
        due_at: hourBefore.toISOString(),
        title: `Rappel dans 1h : ${appointment.title}`,
        message: appointment.location
          ? `${appointment.title} — ${appointment.location}`
          : appointment.title,
      },
    ];

    for (const r of reminders) {
      const { error } = await supabase
        .from('user_reminders')
        .upsert(
          {
            user_id: user.id,
            pet_id: appointment.petId ?? null,
            related_event_id: appointment.id,
            reminder_index: r.reminder_index,
            title: r.title,
            message: r.message,
            due_at: r.due_at,
            status: 'pending',
            reminder_type: appointment.type || 'appointment',
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            metadata: {
              appointment_date: appointment.date,
              appointment_time: appointment.time,
              location: appointment.location,
            },
          },
          { onConflict: 'related_event_id,reminder_index', ignoreDuplicates: false }
        );

      if (error) {
        logger.error('useReminderSync: Erreur upsert rappel', r.reminder_index, error);
      }
    }
  }, [user, deleteReminder]);

  const syncAllReminders = useCallback(async (appointments: Appointment[]) => {
    if (!user) return;
    for (const appt of appointments.filter(a => a.reminderEnabled)) {
      await syncReminder(appt);
    }
  }, [user, syncReminder]);

  return { syncReminder, deleteReminder, syncAllReminders };
}
